import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, UserCircle2, Search } from "lucide-react";
import { toast } from "sonner";

/** Department master with inline HOD (Head of Department) picker and Location association.
 * HOD is stored as an FK to employees.id (backend model: MasterIn.hod_emp_id).
 * location_id links the department to a Hospital / Location (used for scoping + inheritance).
 */
export default function DepartmentsTab({ depts, onChange }) {
  const [name, setName] = useState("");
  const [emps, setEmps] = useState([]);
  const [locs, setLocs] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // dept object being edited
  const [editHod, setEditHod] = useState("");
  const [editLoc, setEditLoc] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/employees", { params: { limit: 500, page: 1 } }),
      api.get("/masters/locations"),
    ]).then(([e, l]) => {
      setEmps(e.data.data || e.data || []);
      setLocs(l.data || []);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!q) return depts;
    const s = q.toLowerCase();
    return depts.filter(d => (d.name || "").toLowerCase().includes(s));
  }, [depts, q]);

  const empById = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e])), [emps]);
  const locById = useMemo(() => Object.fromEntries(locs.map(l => [l.id, l])), [locs]);

  async function add() {
    if (!name.trim()) return;
    await api.post("/masters/departments", { name: name.trim() });
    setName("");
    onChange();
    toast.success("Department added");
  }

  async function save() {
    if (!editing) return;
    await api.put(`/masters/departments/${editing.id}`, {
      name: editing.name,
      hod_emp_id: editHod === "__none__" ? null : (editHod || null),
      location_id: editLoc === "__none__" ? null : (editLoc || null),
    });
    setEditing(null);
    setEditHod("");
    setEditLoc("");
    onChange();
    toast.success("Department updated");
  }

  async function del(id) {
    if (!window.confirm("Delete this department?")) return;
    await api.delete(`/masters/departments/${id}`);
    onChange();
  }

  return (
    <Card className="border-slate-200 rounded-2xl">
      <CardContent className="p-6 space-y-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs uppercase tracking-wider text-slate-500">New department</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Pharmacy" data-testid="dept-input"/>
          </div>
          <Button onClick={add} className="bg-sky-500 hover:bg-sky-600" data-testid="dept-add">
            <Plus className="h-4 w-4 mr-1"/>Add Department
          </Button>
          <div className="ml-auto relative min-w-[220px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="pl-9" data-testid="dept-search"/>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="departments-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="py-3 px-4">Name</th>
                <th className="py-3 px-4">Head of Department (HOD)</th>
                <th className="py-3 px-4">Location / Hospital</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan="4" className="py-10 text-center text-slate-400">No departments.</td></tr>
              )}
              {filtered.map(d => {
                const isEditing = editing?.id === d.id;
                const hod = d.hod_emp_id ? empById[d.hod_emp_id] : null;
                const loc = d.location_id ? locById[d.location_id] : null;
                return (
                  <tr key={d.id} className="border-b border-slate-100">
                    <td className="py-3 px-4">
                      {isEditing ? (
                        <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid={`dept-name-${d.id}`}/>
                      ) : (
                        <span className="font-medium text-slate-900">{d.name}</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {isEditing ? (
                        <Select value={editHod || "__none__"} onValueChange={setEditHod}>
                          <SelectTrigger data-testid={`dept-hod-select-${d.id}`}><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— None —</SelectItem>
                            {emps.map(e => (
                              <SelectItem key={e.id} value={e.id}>{e.name} ({e.emp_id})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : hod ? (
                        <div className="flex items-center gap-2">
                          <UserCircle2 className="h-4 w-4 text-sky-500"/>
                          <span className="font-medium">{hod.name}</span>
                          <span className="text-xs text-slate-400 font-mono">{hod.emp_id}</span>
                        </div>
                      ) : (
                        <span className="text-slate-300 text-xs">— none assigned —</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {isEditing ? (
                        <Select value={editLoc || "__none__"} onValueChange={setEditLoc}>
                          <SelectTrigger data-testid={`dept-loc-select-${d.id}`}><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— None —</SelectItem>
                            {locs.map(l => (
                              <SelectItem key={l.id} value={l.id}>{l.name}{l.code ? ` (${l.code})` : ""}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : loc ? (
                        <span className="inline-flex items-center gap-1.5 text-slate-700">
                          <span className="font-medium">{loc.name}</span>
                          {loc.code && <span className="text-xs font-mono text-slate-400">{loc.code}</span>}
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">— unset —</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {isEditing ? (
                        <div className="inline-flex gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => { setEditing(null); setEditHod(""); setEditLoc(""); }}>Cancel</Button>
                          <Button size="sm" onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`dept-save-${d.id}`}>Save</Button>
                        </div>
                      ) : (
                        <div className="inline-flex gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => { setEditing(d); setEditHod(d.hod_emp_id || ""); setEditLoc(d.location_id || ""); }} data-testid={`dept-edit-${d.id}`}>Edit</Button>
                          <Button size="sm" variant="ghost" onClick={() => del(d.id)} data-testid={`dept-delete-${d.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500"/>
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
